import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  IconButton,
  useColorModeValue,
  useToast,
  Flex,
  Stack,
  Collapse,
} from '@chakra-ui/react';
import {
  FiUploadCloud,
  FiFile,
  FiImage,
  FiFileText,
  FiX,
  FiLink2,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { uploadFile as defaultUploadFile } from '../../services/articles';

export type UploadPanelFile = {
  url: string;
  name: string;
  type: string;
  size: number;
};

export interface UploadPanelProps {
  label?: string;
  description?: string;
  value?: UploadPanelFile[];
  onChange?: (files: UploadPanelFile[]) => void;
  accept?: string;
  maxFiles?: number;
  maxFileSizeMB?: number;
  multiple?: boolean;
  uploadFn?: (file: File) => Promise<UploadPanelFile>;
  allowUrlImport?: boolean;
  onUrlImport?: (url: string) => void | Promise<void>;
  urlPlaceholder?: string;
  onUploadFinished?: (uploadedFiles: UploadPanelFile[], allFiles: UploadPanelFile[]) => void;
}

const DEFAULT_MAX_MB = 10;

const UploadPanel: React.FC<UploadPanelProps> = ({
  label = 'Nahrát soubory',
  description = 'Vyberte soubory k nahrání nebo je přetáhněte do oblasti níže.',
  value,
  onChange,
  accept = 'image/*,application/pdf',
  maxFiles = 10,
  maxFileSizeMB = DEFAULT_MAX_MB,
  multiple = true,
  uploadFn,
  allowUrlImport = true,
  onUrlImport,
  urlPlaceholder = 'Vložit URL souboru',
  onUploadFinished,
}) => {
  const [files, setFiles] = useState<UploadPanelFile[]>(() => value || []);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bg = useColorModeValue('white', 'gray.800');
  const dropBg = useColorModeValue('gray.50', 'gray.900');
   const listHoverBg = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    if (value) {
      setFiles(value);
    }
  }, [value]);

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return FiImage;
    if (type.includes('pdf')) return FiFileText;
    return FiFile;
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  const emitChange = useCallback(
    (next: UploadPanelFile[]) => {
      setFiles(next);
      if (onChange) onChange(next);
    },
    [onChange]
  );

  const doUpload = useCallback(
    async (file: File): Promise<UploadPanelFile> => {
      if (uploadFn) {
        return uploadFn(file);
      }
      const res = await defaultUploadFile(file as any);
      return {
        url: res.url,
        name: file.name,
        type: file.type || res.type || 'application/octet-stream',
        size: file.size || res.size || 0,
      };
    },
    [uploadFn]
  );

  const handleFiles = useCallback(
    async (selected: FileList | File[]) => {
      const list = Array.from(selected || []);
      if (!list.length) return;

      if (files.length + list.length > maxFiles) {
        toast({
          title: 'Příliš mnoho souborů',
          description: `Můžete nahrát maximálně ${maxFiles} souborů`,
          status: 'warning',
        });
        return;
      }

      const maxBytes = maxFileSizeMB * 1024 * 1024;
      const validFiles = list.filter((f) => {
        if (f.size > maxBytes) {
          toast({
            title: 'Soubor je příliš velký',
            description: `${f.name} překračuje limit ${maxFileSizeMB} MB`,
            status: 'error',
          });
          return false;
        }
        return true;
      });
      if (!validFiles.length) return;

      setUploading(true);
      setProgress(0);

      const newlyUploaded: UploadPanelFile[] = [];
      const total = validFiles.length;

      try {
        for (let i = 0; i < validFiles.length; i += 1) {
          const f = validFiles[i];
          try {
            const uf = await doUpload(f);
            newlyUploaded.push(uf);
          } catch (err: any) {
            toast({
              title: 'Chyba při nahrávání',
              description: err?.message || f.name,
              status: 'error',
            });
          }
          setProgress(((i + 1) / total) * 100);
        }
        if (newlyUploaded.length) {
          const next = [...files, ...newlyUploaded];
          emitChange(next);
          if (onUploadFinished) {
            onUploadFinished(newlyUploaded, next);
          }
          toast({
            title: 'Nahrávání dokončeno',
            description: `${newlyUploaded.length} souborů bylo nahráno`,
            status: 'success',
          });
        }
      } finally {
        setUploading(false);
        setProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [doUpload, emitChange, files, maxFileSizeMB, maxFiles, onUploadFinished, toast]
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const removeFile = (idx: number) => {
    const next = files.filter((_, i) => i !== idx);
    emitChange(next);
  };

  const handleUrlSubmit = async () => {
    const url = urlInput.trim();
    if (!url) return;
    try {
      if (onUrlImport) {
        await onUrlImport(url);
      }
      const next: UploadPanelFile[] = [
        ...files,
        {
          url,
          name: url,
          type: 'text/url',
          size: 0,
        },
      ];
      emitChange(next);
      setUrlInput('');
      toast({ title: 'URL přidána', status: 'success', duration: 2000 });
    } catch (err: any) {
      toast({
        title: 'Nelze přidat URL',
        description: err?.message || 'Zkontrolujte odkaz a zkuste to znovu.',
        status: 'error',
      });
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      borderColor={borderColor}
      bg={bg}
      p={4}
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="flex-start">
          <Box>
            <HStack spacing={2} mb={1}>
              <Icon as={FiUploadCloud} color="blue.500" />
              <Text fontWeight="semibold">{label}</Text>
            </HStack>
            <Text fontSize="sm" color="gray.500">
              {description}
            </Text>
          </Box>
          {files.length > 0 && (
            <HStack spacing={1} align="center" color="gray.500" fontSize="xs">
              <Icon as={FiCheckCircle} color="green.400" />
              <Text>{files.length} souborů</Text>
            </HStack>
          )}
        </HStack>

        <Box
          borderWidth="1px"
          borderRadius="lg"
          borderStyle={isDragging ? 'solid' : 'dashed'}
          borderColor={isDragging ? 'blue.400' : borderColor}
          bg={isDragging ? dropBg : 'transparent'}
          p={6}
          textAlign="center"
          cursor="pointer"
          transition="all 0.15s ease-out"
          onClick={() => fileInputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <VStack spacing={2}>
            <Icon as={FiUploadCloud} boxSize={7} color="blue.400" />
            <Text fontWeight="medium">Zvolte soubor nebo jej přetáhněte sem</Text>
            <Text fontSize="xs" color="gray.500">
              Podporované formáty: {accept || 'soubor'} • Limit {maxFileSizeMB} MB / soubor
            </Text>
            <Button size="sm" variant="outline" leftIcon={<FiUploadCloud />}>
              Vybrat soubor
            </Button>
          </VStack>
          <input
            ref={fileInputRef}
            type="file"
            multiple={multiple}
            accept={accept}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
            }}
          />
        </Box>

        <Collapse in={uploading || progress > 0} animateOpacity>
          {uploading && (
            <HStack spacing={2} align="center">
              <Icon as={FiAlertCircle} color="blue.400" />
              <Text fontSize="sm">Nahrávám soubory…</Text>
            </HStack>
          )}
          {progress > 0 && (
            <Progress mt={2} value={progress} size="sm" colorScheme="blue" borderRadius="full" />
          )}
        </Collapse>

        {files.length > 0 && (
          <Box borderTopWidth="1px" borderColor={borderColor} pt={3}>
            <Stack spacing={2}>
              {files.map((f, idx) => {
                const IconComp = getFileIcon(f.type || '');
                return (
                  <Flex
                    key={`${f.url}-${idx}`}
                    align="center"
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    _hover={{ bg: listHoverBg }}
                  >
                    <HStack spacing={3} flex={1} minW={0}>
                      <Icon as={IconComp} boxSize={5} color="blue.500" />
                      <Box flex={1} minW={0}>
                        <Text fontSize="sm" noOfLines={1}>
                          {f.name || f.url}
                        </Text>
                        <HStack spacing={2} fontSize="xs" color="gray.500">
                          {f.size ? <Text>{formatSize(f.size)}</Text> : null}
                          <Text>{f.type}</Text>
                        </HStack>
                      </Box>
                    </HStack>
                    <IconButton
                      aria-label="Odebrat soubor"
                      icon={<FiX />}
                      size="xs"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(idx);
                      }}
                    />
                  </Flex>
                );
              })}
            </Stack>
          </Box>
        )}

        {allowUrlImport && (
          <Box pt={2} borderTopWidth={files.length ? '1px' : '0'} borderColor={borderColor}>
            <Text fontSize="xs" color="gray.500" mb={1}>
              nebo importujte z URL odkazu
            </Text>
            <HStack spacing={2}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiLink2} color="gray.400" />
                </InputLeftElement>
                <Input
                  size="sm"
                  placeholder={urlPlaceholder}
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleUrlSubmit();
                    }
                  }}
                />
              </InputGroup>
              <Button size="sm" onClick={handleUrlSubmit} isDisabled={!urlInput.trim()}>
                Přidat
              </Button>
            </HStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default UploadPanel;
